import { getLanguages } from '@mews-commander/core/legacy-globalization';
import { DisplayField, Membership, Sex, TitlePrefix } from '@mews-commander/guest-profile';
import { getCountryTextKey } from '@mews-commander/legacy-entities/src/ducks/countries';
import { Dash } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedEnum, LocalizedWallClockDate, lt, WallClockDateService } from '@mews/framework';
import { Optional } from '@mews/std';
import { Typography } from '@optimus-web/core';

interface ValueComponentProps extends Omit<GetContentProps, 'value'> {
    value: Optional<string>;
}
const isSex = (value: Sex | string | null | undefined): value is Sex => {
    if (!value) return false;
    return Object.values(Sex).includes(value as Sex);
};

const isTitle = (value: TitlePrefix | string | null | undefined): value is TitlePrefix => {
    if (!value) return false;
    return Object.values(TitlePrefix).includes(value as TitlePrefix);
};

const LanguageName = ({ languageCode }: { languageCode: string }) => {
    const languages = useCommanderSelector(getLanguages);
    // @ts-expect-error Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
    const languageEnglishName = languages[languageCode]?.englishName;
    return <>{languageEnglishName}</>;
};

const loyaltyMembershipField = (loyaltyMembership?: Membership) =>
    loyaltyMembership?.code
        ? `${`${UnicodeEntity.LeftParenthesis}${loyaltyMembership.program?.code}${UnicodeEntity.RightParenthesis}`}${
              UnicodeEntity.NoBreakSpace
          }${loyaltyMembership?.code}`
        : `${UnicodeEntity.LeftParenthesis}${loyaltyMembership?.program?.code}${UnicodeEntity.RightParenthesis}`;

interface GetContentProps {
    loyaltyMembership?: Membership | null;
    type: DisplayField;
    value: string;
}
const getContent = ({ value, type, loyaltyMembership }: GetContentProps) => {
    switch (type) {
        case DisplayField.BirthDate:
            return <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />;
        case DisplayField.LoyaltyMembership:
            return loyaltyMembership ? loyaltyMembershipField(loyaltyMembership) : null;
        case DisplayField.NationalityCode:
            return lt(getCountryTextKey(value));
        case DisplayField.PreferredLanguageCode:
            return <LanguageName languageCode={value} />;
        case DisplayField.Sex:
            return isSex(value) ? <LocalizedEnum prefix="Sex" value={value} /> : value;
        case DisplayField.TitlePrefix:
            // @ts-expect-error TitlePrefix from data-access needs change from Misses to Mrs
            return isTitle(value) ? <LocalizedEnum prefix="TitlePrefix" value={value} /> : value;
        default:
            return value;
    }
};

export const ValueComponent = ({ value, ...props }: ValueComponentProps) => {
    if (!value) {
        return (
            <Typography textStyle="bodyMedium" as="div">
                <Dash />
            </Typography>
        );
    }

    return (
        <Typography textStyle="bodyMedium" as="div">
            {getContent({ value, ...props })}
        </Typography>
    );
};
